import * as React from 'react';
import { Button } from 'antd';
import { IWidget } from 'common/schema/widget/Widget';
import { WidgetInputRow } from './row/WidgetInputRow';
import { useWidgetListCompile } from '../../form/nested/useWidgetListCompile';
import { useFormikContext } from 'formik';
import {
  DragDropContext,
  Droppable,
  DropResult,
  ResponderProvided
} from 'react-beautiful-dnd';
import { useCallback, useEffect } from 'react';
import { MetadataLogic } from 'common/schema/metadata/MetadataLogic';
import { FormWidgetItem } from '../../form/wrapper/FormWidgetItem';
import { FormikForm } from 'client/ui/form/FormikForm';
import { PlusOutlined } from '@ant-design/icons';
import { PreferencesAddButtonWrapper } from './row/PreferencesAddButtonWrapper';
import { handleDragAndDrop } from './util/handleDragAndDrop';
import { handleShowButton } from './util/handleShowButtons';

interface FormWidgetInputMultiPreferenceViewProps {
  widget: IWidget;
}

/**
 * Rendering del Widget di input a preferenza multipla. Il widget è composto da una lista di righe
 */
export function FormWidgetInputMultiPreferenceView(
  props: FormWidgetInputMultiPreferenceViewProps
) {
  const { widget } = props;
  const { rows, addRow, removeRow } = useWidgetListCompile(widget, true);

  const formik = useFormikContext<IWidget>();
  const metadata = MetadataLogic.singleFromWidget(widget);
  const showAddButton = handleShowButton(rows, widget);

  // Gestione del Drag&Drop
  const onDragEnd = useCallback(
    (result: DropResult, provided: ResponderProvided) => {
      handleDragAndDrop(rows, result, metadata.id, formik);
    },
    [rows]
  );

  // Verifico se presente un numero di preferenze massimo
  const maxRows = widget.input?.validation.maxrows ?? null;

  // Aggiunta di una nuova riga al primo caricamento
  useEffect(() => {
    if (!rows || rows.length === 0) {
      addRow();
    }
  }, []);

  return (
    <FormWidgetItem
      // Il name del FormWidgetItem presenta "no-validation" per evitare che venga validato da formik
      name={`values.${metadata.id}-no-validation`}
      widget={widget}
      label={widget.input?.label ?? 'Scelta Preferenze'}
      validateStatus={undefined}
    >
      <FormikForm helpInTooltips>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="List">
            {(provided, _) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {[...rows].map((row, index) => {
                  return (
                    <WidgetInputRow
                      row={row}
                      key={index}
                      index={index}
                      widget={widget}
                      handleAdd={addRow}
                      handleRemove={removeRow}
                    />
                  );
                })}
                {provided.placeholder}

                {showAddButton && (
                  <PreferencesAddButtonWrapper>
                    <Button
                      disabled={maxRows ? rows.length >= maxRows : false}
                      block
                      ghost
                      icon={<PlusOutlined />}
                      type="primary"
                      onClick={() => addRow()}
                    >
                      Aggiungi Preferenza
                    </Button>
                  </PreferencesAddButtonWrapper>
                )}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </FormikForm>
    </FormWidgetItem>
  );
}
